import React, { useEffect, useState } from "react";
import { Menu, X, ArrowRight, Mail, Phone } from "lucide-react";

const links = [
  { href: "#uslugi", label: "Usługi" },
  { href: "#portfolio", label: "Portfolio" },
  { href: "#opinie", label: "Opinie" },
  { href: "#proces", label: "Proces" },
  { href: "#faq", label: "FAQ" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled || open
            ? "backdrop-blur-xl bg-[#1A1A2E]/75 border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={closeMenu}
            className="flex items-center gap-2 group relative z-[60]"
          >
            <span className="relative inline-flex h-8 w-8 items-center justify-center">
              <span className="absolute inset-0 rounded-md bg-stormly-cyan/20 group-hover:bg-stormly-cyan/40 transition-colors" />
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="relative h-[18px] w-[18px] text-stormly-cyan"
              >
                <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill="currentColor" />
              </svg>
            </span>
            <span className="font-heading font-black text-2xl tracking-tight leading-none">
              Stormly
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-white/80 hover:text-white link-underline transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <a
            href="#kontakt"
            data-testid="nav-cta"
            className="hidden md:inline-flex btn-primary text-xs py-2 px-4"
          >
            Darmowa wycena
          </a>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden relative z-[60] h-10 w-10 -mr-2 flex items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur-md text-white hover:border-stormly-cyan/40 hover:text-stormly-cyan transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={open}
          >
            <span className="relative inline-block h-4 w-5">
              <span
                className={`absolute left-0 top-0 h-0.5 w-full bg-current transition-transform duration-300 ${
                  open ? "translate-y-1.5 rotate-45" : ""
                }`}
              />
              <span
                className={`absolute left-0 top-1.5 h-0.5 w-full bg-current transition-opacity duration-200 ${
                  open ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`absolute left-0 top-3 h-0.5 w-full bg-current transition-transform duration-300 ${
                  open ? "-translate-y-1.5 -rotate-45" : ""
                }`}
              />
            </span>
          </button>
        </div>
      </header>

      {/* Mobile menu - full-screen overlay */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu-overlay"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-[#0d0d1f]/85 backdrop-blur-2xl"
          onClick={closeMenu}
        />

        {/* Decorative orbs */}
        <div
          className="absolute -top-20 -right-20 h-72 w-72 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(0,255,255,0.18), transparent 70%)",
            filter: "blur(40px)",
          }}
        />
        <div
          className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(0,180,255,0.12), transparent 70%)",
            filter: "blur(40px)",
          }}
        />

        {/* Content */}
        <div
          data-testid="mobile-menu"
          className={`relative h-full flex flex-col px-6 pt-24 pb-10 transition-transform duration-500 ${
            open ? "translate-y-0" : "-translate-y-4"
          }`}
        >
          <nav className="flex-1 flex flex-col">
            {links.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                onClick={closeMenu}
                style={{
                  transitionDelay: open ? `${i * 70 + 100}ms` : "0ms",
                }}
                className={`group relative py-5 border-b border-white/10 flex items-center justify-between transition-all duration-500 ${
                  open
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-4"
                }`}
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
              >
                <span className="font-heading font-bold text-3xl text-white group-hover:text-stormly-cyan transition-colors">
                  {l.label}
                </span>
                <ArrowRight
                  size={20}
                  className="text-white/40 group-hover:text-stormly-cyan group-hover:translate-x-1 transition-all"
                />
              </a>
            ))}
          </nav>

          <div
            className={`mt-8 transition-all duration-500 ${
              open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
            style={{ transitionDelay: open ? "450ms" : "0ms" }}
          >
            <a
              href="#kontakt"
              onClick={closeMenu}
              className="btn-primary w-full justify-center"
              data-testid="mobile-cta"
            >
              Darmowa wycena
              <ArrowRight size={18} />
            </a>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <a
                href="tel:+48507890059"
                onClick={closeMenu}
                className="flex items-center gap-2 px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white/80 hover:border-stormly-cyan/40 hover:text-stormly-cyan transition-colors"
              >
                <Phone size={14} />
                507 890 059
              </a>
              <a
                href="mailto:stormly.info@gmail.com"
                onClick={closeMenu}
                className="flex items-center gap-2 px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white/80 hover:border-stormly-cyan/40 hover:text-stormly-cyan transition-colors truncate"
              >
                <Mail size={14} className="shrink-0" />
                <span className="truncate">E-mail</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
